import type { ReactNode } from "react";
import { usePrefersReducedMotion } from "../hooks/usePrefersReducedMotion";

type ExpandingSectionProps = {
  /** Stan otwarcia - kontrolowany z zewnątrz (przycisk konsumenta ma aria-expanded) */
  open: boolean;
  children: ReactNode;
  durationMs?: number;
  className?: string;
};

/**
 * Płynne rozwijanie treści o nieznanej wysokości - technika grid-template-rows
 * 0fr → 1fr (zero mierzenia w JS, działa z dynamiczną treścią, czysty CSS).
 * Treść dodatkowo delikatnie wjeżdża (opacity + translateY) z małym opóźnieniem,
 * żeby pojawiała się już w otwierającej się przestrzeni, nie "przed nią".
 *
 * JEDEN komponent - CZTERY zastosowania na stronie (spójność wzorca):
 *  1. Formularz kontaktowy: kroki 02/03 rozwijają się po wyborze ścieżki
 *  2. RODO pod formularzem: jedna linia + "rozwiń pełną informację"
 *  3. Opinie: "CZYTAJ WIĘCEJ" rozwija pełny cytat w miejscu
 *  4. Oferty pracy: "ZOBACZ SZCZEGÓŁY" rozwija opis roli w wierszu
 *     (strona obiecuje "bez przeskakiwania zakładek" - ten wzorzec to dowozi)
 *
 * Dostępność: aria-hidden na zwiniętej treści; element sterujący po stronie
 * konsumenta MUSI mieć aria-expanded={open} i aria-controls.
 * Reduced-motion: natychmiastowe pokazanie/ukrycie, bez animacji.
 *
 * Użycie:
 *   const [open, setOpen] = useState(false);
 *   <button aria-expanded={open} aria-controls="rodo" onClick={() => setOpen(!open)}>
 *     Informacja o przetwarzaniu danych {open ? "−" : "+"}
 *   </button>
 *   <ExpandingSection open={open}><div id="rodo">...pełne RODO...</div></ExpandingSection>
 */
export function ExpandingSection({ open, children, durationMs = 550, className }: ExpandingSectionProps) {
  const reducedMotion = usePrefersReducedMotion();
  const ease = "cubic-bezier(0.16, 1, 0.3, 1)";

  return (
    <div
      className={className}
      style={{
        display: "grid",
        gridTemplateRows: open ? "1fr" : "0fr",
        transition: reducedMotion ? undefined : `grid-template-rows ${durationMs}ms ${ease}`,
      }}
    >
      <div style={{ overflow: "hidden", minHeight: 0 }} aria-hidden={!open}>
        <div
          style={{
            opacity: open ? 1 : 0,
            transform: open ? "translateY(0)" : "translateY(10px)",
            transition: reducedMotion
              ? undefined
              : `opacity ${Math.round(durationMs * 0.7)}ms ease ${open ? Math.round(durationMs * 0.25) : 0}ms, transform ${durationMs}ms ${ease} ${open ? Math.round(durationMs * 0.15) : 0}ms`,
          }}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
